import { useEffect, useRef } from 'react';

/**
 * Fires `flush()` when the user is about to lose in-flight work — window
 * blur, tab hidden (visibilitychange), or page unload. `shouldFlush` is
 * checked first so we never trigger no-op saves.
 *
 * On `beforeunload` with pending work, also asks the browser to show its
 * "Leave site? Changes you made may not be saved." confirmation so the
 * user can choose to stay until the save completes. Modern browsers
 * ignore custom messages and render their own generic dialog regardless
 * of `returnValue` contents.
 *
 * Shared by autosave hooks that need the same safety-flush behavior
 * (see `useAutosave` and `useQuoteCardAutoSave`).
 */
export function useFlushOnLeave(
  flush: () => Promise<void> | void,
  shouldFlush: () => boolean,
) {
  const flushRef = useRef(flush);
  flushRef.current = flush;
  const shouldFlushRef = useRef(shouldFlush);
  shouldFlushRef.current = shouldFlush;

  useEffect(() => {
    const onBlur = () => {
      if (shouldFlushRef.current()) void flushRef.current();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && shouldFlushRef.current()) {
        void flushRef.current();
      }
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldFlushRef.current()) return;
      event.preventDefault();
      event.returnValue = '';
      void flushRef.current();
    };

    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);
}
